import { type BeforeSendFn, type PostHogConfig } from "posthog-js";
import { type AuthClient } from "~/api/AuthClient";
import { DEBUG_TRACKING, DISABLED_TRACKING_DOMAINS } from "~/settings";

// Synthetic monitors and headless browsers that should not contribute to analytics or recordings.
const BOT_USER_AGENTS = [
  "HeadlessChrome",
  "Pingdom",
  "UptimeRobot",
  "NewRelicPinger",
];

/*
Disable sending data for domains like 'di.local'.
Docs: https://posthog.com/docs/libraries/js/usage#amending-or-sampling-events
Note: Although autocapture has a `url_ignorelist` defined, it does not seem to work.
If we at some point want to supply an allowlist we could test out `autocapture.url_allowlist`,
but this has an added level of maintenance.
 */
export const createBeforeSend =
  (authClient: AuthClient): BeforeSendFn =>
  (event) => {
    if (!event) return null;
    // Autocaptured pageviews, exceptions and surveys never pass through
    // useTracking, so impersonated sessions are dropped at this one choke
    // point. Read from the AuthClient rather than a copy, so a reload during
    // an impersonated session is covered as soon as the token comes back.
    if (authClient.user?.isImpersonated) return null;
    if (DISABLED_TRACKING_DOMAINS.includes(location.hostname)) return null;
    // navigator.webdriver is true in Playwright, Puppeteer, and Selenium — undefined in real browsers.
    if (navigator.webdriver) return null;
    return event;
  };

export const landingCampaignParams = (url: string) =>
  Object.fromEntries(
    [...new URL(url).searchParams].filter(([key]) => key.startsWith("utm_")),
  );

// PostHog reads utm parameters from the url at its first capture, which can be after a route redirect has
// already dropped them (e.g. an agent page replacing the search with the agent's filter). Read them at load.
const LANDING_CAMPAIGN_PARAMS = landingCampaignParams(window.location.href);

export const createPostHogConfig = (
  authClient: AuthClient,
): Partial<PostHogConfig> => ({
  api_host: import.meta.env.VITE_POSTHOG_HOST,
  defaults: "2025-05-24",
  custom_blocked_useragents: BOT_USER_AGENTS,
  before_send: createBeforeSend(authClient),
  loaded: (posthog) => {
    if (Object.keys(LANDING_CAMPAIGN_PARAMS).length) {
      posthog.register(LANDING_CAMPAIGN_PARAMS);
    }
  },
  disable_persistence: true, // Our persistence is the logged in user.
  persistence: "memory", // disable_persistence does still seem to save some data.
  capture_pageview: "history_change", // SPAs work on history changes.
  capture_exceptions: true, // This enables capturing exceptions using Error Tracking.
  disable_surveys_automatic_display: true, // We control when and how the surveys are displayed.
  debug: DEBUG_TRACKING, // Can also be enabled by appending `?__posthog_debug=true` at the end of your URL, or call `posthog.debug()` in your code.
});
